import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

// Type definition for PerformanceMemory
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

// Performance metrics types
export interface PerformanceMetrics {
  FCP: number | null; // First Contentful Paint
  LCP: number | null; // Largest Contentful Paint
  FID: number | null; // First Input Delay (now INP)
  CLS: number | null; // Cumulative Layout Shift
  TTFB: number | null; // Time to First Byte
  JS_HEAP: number | null; // JavaScript heap size
  DOM_NODES: number | null; // DOM node count
  RESOURCES: number | null; // Resource count
  FPS: number | null; // Frames per second
}

// Performance thresholds by page tier
export const PERFORMANCE_THRESHOLDS = {
  0: { // Homepage
    LCP: 2500,
    FID: 100,
    CLS: 0.1,
    FPS: 30
  },
  1: { // Main sections
    LCP: 2000,
    FID: 100,
    CLS: 0.1,
    FPS: 45
  },
  2: { // Category hubs
    LCP: 1500,
    FID: 100,
    CLS: 0.1,
    FPS: 55
  },
  3: { // Content pages
    LCP: 1000,
    FID: 100,
    CLS: 0.1,
    FPS: 60
  }
};

// Performance monitoring class
export class PerformanceMonitor {
  private metrics: PerformanceMetrics = {
    FCP: null,
    LCP: null,
    FID: null,
    CLS: null,
    TTFB: null,
    JS_HEAP: null,
    DOM_NODES: null,
    RESOURCES: null,
    FPS: null
  };
  
  private fpsFrames: number[] = [];
  private lastFrameTime = performance.now();
  private rafId: number | null = null;
  private observers: Set<(metrics: PerformanceMetrics) => void> = new Set();
  private pageTier: number = 0;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initializeWebVitals();
      this.startFPSMonitoring();
      this.startResourceMonitoring();
      this.detectPageTier();
    }
  }

  private detectPageTier(): void {
    const pathname = window.location.pathname;
    if (pathname === '/') {
      this.pageTier = 0;
    } else {
      const segments = pathname.split('/').filter(Boolean);
      this.pageTier = Math.min(segments.length, 3);
    }
  }

  private initializeWebVitals(): void {
    // Collect Web Vitals metrics
    onCLS((metric) => {
      this.metrics.CLS = metric.value;
      this.notifyObservers();
    });

    onFCP((metric) => {
      this.metrics.FCP = metric.value;
      this.notifyObservers();
    });

    onINP((metric) => {
      this.metrics.FID = metric.value;
      this.notifyObservers();
    });

    onLCP((metric) => {
      this.metrics.LCP = metric.value;
      this.notifyObservers();
      this.checkPerformanceThresholds();
    });

    onTTFB((metric) => {
      this.metrics.TTFB = metric.value;
      this.notifyObservers();
    });
  }

  private startFPSMonitoring(): void {
    const measureFPS = () => {
      const currentTime = performance.now();
      const deltaTime = currentTime - this.lastFrameTime;
      this.lastFrameTime = currentTime;

      // Calculate FPS
      const fps = 1000 / deltaTime;
      this.fpsFrames.push(fps);

      // Keep only last 60 frames
      if (this.fpsFrames.length > 60) {
        this.fpsFrames.shift();
      }

      // Calculate average FPS
      const avgFPS = this.fpsFrames.reduce((a, b) => a + b, 0) / this.fpsFrames.length;
      this.metrics.FPS = Math.round(avgFPS);

      this.rafId = requestAnimationFrame(measureFPS);
    };

    measureFPS();
  }

  private startResourceMonitoring(): void {
    const updateResourceMetrics = () => {
      // JavaScript heap size
      if ('memory' in performance && (performance as { memory?: PerformanceMemory }).memory) {
        const memory = (performance as { memory: PerformanceMemory }).memory;
        this.metrics.JS_HEAP = Math.round(memory.usedJSHeapSize / 1048576); // Convert to MB
      }

      // DOM node count
      this.metrics.DOM_NODES = document.getElementsByTagName('*').length;

      // Resource count
      const resources = performance.getEntriesByType('resource');
      this.metrics.RESOURCES = resources.length;

      this.notifyObservers();
    };

    // Update every 5 seconds
    setInterval(updateResourceMetrics, 5000);
    updateResourceMetrics();
  }

  private checkPerformanceThresholds(): void {
    const thresholds = PERFORMANCE_THRESHOLDS[this.pageTier as keyof typeof PERFORMANCE_THRESHOLDS];
    
    if (!thresholds) {
      return;
    }

    const warnings: string[] = [];

    if (this.metrics.LCP && this.metrics.LCP > thresholds.LCP) {
      warnings.push(`LCP (${this.metrics.LCP}ms) exceeds threshold (${thresholds.LCP}ms)`);
    }

    if (this.metrics.FID && this.metrics.FID > thresholds.FID) {
      warnings.push(`FID (${this.metrics.FID}ms) exceeds threshold (${thresholds.FID}ms)`);
    }

    if (this.metrics.CLS && this.metrics.CLS > thresholds.CLS) {
      warnings.push(`CLS (${this.metrics.CLS}) exceeds threshold (${thresholds.CLS})`);
    }

    if (this.metrics.FPS && this.metrics.FPS < thresholds.FPS) {
      warnings.push(`FPS (${this.metrics.FPS}) below threshold (${thresholds.FPS})`);
    }

    if (warnings.length > 0) {
      // Performance warnings detected
      this.triggerPerformanceOptimizations();
    }
  }

  private triggerPerformanceOptimizations(): void {
    // Reduce animation complexity
    if (this.metrics.FPS && this.metrics.FPS < 30) {
      document.body.classList.add('reduce-motion');
      // Reducing animation complexity due to low FPS
    }

    // Lazy load images more aggressively
    if (this.metrics.LCP && this.metrics.LCP > 3000) {
      const images = document.querySelectorAll('img:not([loading="lazy"])');
      images.forEach(img => img.setAttribute('loading', 'lazy'));
      // Enabling lazy loading for all images
    }

    // Reduce DOM complexity
    if (this.metrics.DOM_NODES && this.metrics.DOM_NODES > 1500) {
      // High DOM node count detected
    }
  }

  private notifyObservers(): void {
    this.observers.forEach(observer => observer(this.metrics));
  }

  public subscribe(callback: (metrics: PerformanceMetrics) => void): () => void {
    this.observers.add(callback);
    // Immediately call with current metrics
    callback(this.metrics);
    
    // Return unsubscribe function
    return () => {
      this.observers.delete(callback);
    };
  }

  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  public reportToAnalytics(): void {
    // Send metrics to analytics service
    if (typeof window !== 'undefined' && (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag) {
      const metrics = this.getMetrics();
      
      // Report Web Vitals
      if (metrics.LCP) {
        (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('event', 'LCP', {
          event_category: 'Web Vitals',
          value: Math.round(metrics.LCP),
          page_tier: this.pageTier
        });
      }

      if (metrics.FID) {
        (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('event', 'FID', {
          event_category: 'Web Vitals',
          value: Math.round(metrics.FID),
          page_tier: this.pageTier
        });
      }

      if (metrics.CLS) {
        (window as unknown as { gtag: (...args: unknown[]) => void }).gtag('event', 'CLS', {
          event_category: 'Web Vitals',
          value: Math.round(metrics.CLS * 1000),
          page_tier: this.pageTier
        });
      }
    }
  }

  public logMetrics(): void {
    // Performance metrics logging can be added here if needed
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Performance] Current metrics:', {
        page_tier: this.pageTier,
        resource_timing: 'performance monitoring active'
      });
    }
  }

  public destroy(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.observers.clear();
  }
}

// Create singleton instance
let performanceMonitorInstance: PerformanceMonitor | null = null;

export function getPerformanceMonitor(): PerformanceMonitor {
  if (!performanceMonitorInstance && typeof window !== 'undefined') {
    performanceMonitorInstance = new PerformanceMonitor();
  }
  return performanceMonitorInstance!;
}

// Performance optimization utilities
export const performanceUtils = {
  // Debounce function for expensive operations
  debounce<T extends (...args: unknown[]) => void>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;
    
    return (...args: Parameters<T>) => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => func(...args), wait);
    };
  },

  // Throttle function for rate limiting
  throttle<T extends (...args: unknown[]) => void>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle = false;
    
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // Request idle callback with fallback
  requestIdleCallback(
    callback: () => void,
    options?: { timeout?: number }
  ): void {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, options);
    } else {
      setTimeout(callback, 1);
    }
  },

  // Measure function execution time
  measureExecutionTime<T extends (...args: unknown[]) => unknown>(
    func: T,
    _label: string
  ): T {
    return ((...args: Parameters<T>) => {
      performance.now(); // Start timing
      const result = func(...args);
      performance.now(); // End timing
      // Performance measurement complete
      return result;
    }) as T;
  }
};

// Export performance marks for custom measurements
export function markPerformance(markName: string): void {
  if (typeof window !== 'undefined' && 'performance' in window) {
    performance.mark(markName);
  }
}

export function measurePerformance(
  measureName: string,
  startMark: string,
  endMark?: string
): void {
  if (typeof window !== 'undefined' && 'performance' in window) {
    if (endMark) {
      performance.measure(measureName, startMark, endMark);
    } else {
      performance.mark(`${measureName}-end`);
      performance.measure(measureName, startMark, `${measureName}-end`);
    }
    
    // Log the measurement
    const measures = performance.getEntriesByName(measureName, 'measure');
    if (measures.length > 0 && measures[0]) {
      // Performance measurement recorded
    }
  }
}